import { format } from 'date-fns';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import { X } from 'lucide-react';
import { type ComponentType } from 'react';

import { Button } from '@apps/web/components/ui/button';

// A generic datetime-range filter pill styled to sit alongside FilterBar
// chips on admin pages. Values are UTC ISO instants (so shared URLs pin the
// same moment in every timezone); the inputs edit them as wall-clock time in
// `timeZone` when given (for pages standardized on one zone, e.g. the
// ET-bucketed outreach dashboard), or the viewer's local zone otherwise.

export type DateTimeRange = {
    readonly after: string | undefined;
    readonly before: string | undefined;
};

const INPUT_FORMAT = "yyyy-MM-dd'T'HH:mm";

function toDatetimeInputValue(
    iso: string | undefined,
    timeZone: string | undefined,
): string {
    if (!iso) {
        return '';
    }
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    return timeZone
        ? formatInTimeZone(date, timeZone, INPUT_FORMAT)
        : format(date, INPUT_FORMAT);
}

// Range bounds snap to whole hours: the native picker seeds the current
// wall-clock time, and minute-level bounds are noise for these filters.
function floorMinutes(value: string): string {
    return value.replace(/T(\d{2}):\d{2}(?::\d{2}(?:\.\d+)?)?$/, 'T$1:00');
}

function parseDatetimeInputValue(
    value: string,
    timeZone: string | undefined,
): string | undefined {
    if (!value) {
        return undefined;
    }
    const flooredValue = floorMinutes(value);
    const date = timeZone
        ? fromZonedTime(flooredValue, timeZone)
        : new Date(flooredValue);
    return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}

function DateTimeBoundInput({
    label,
    value,
    timeZone,
    onCommit,
}: {
    readonly label: string;
    readonly value: string | undefined;
    readonly timeZone: string | undefined;
    readonly onCommit: (iso: string | undefined) => void;
}) {
    const inputValue = toDatetimeInputValue(value, timeZone);
    // The input is uncontrolled and commits the DOM value on blur/Enter: a
    // controlled value wipes the browser's partially-edited segments (which
    // report value="" until complete), and committing per change would fire a
    // fetch on every segment keystroke. The key remounts the input whenever
    // the committed value changes externally (clear button, back/forward).
    const commit = (input: HTMLInputElement) => {
        onCommit(parseDatetimeInputValue(input.value, timeZone));
    };
    return (
        <input
            key={inputValue}
            type="datetime-local"
            aria-label={label}
            defaultValue={inputValue}
            // Snap picker-seeded minutes to :00 as soon as the value is
            // complete, not only at commit, so the user sees the snap.
            onChange={(event) => {
                const input = event.currentTarget;
                const floored = floorMinutes(input.value);
                if (floored !== input.value) {
                    input.value = floored;
                }
            }}
            onBlur={(event) => commit(event.currentTarget)}
            onKeyDown={(event) => {
                if (event.key === 'Enter') {
                    commit(event.currentTarget);
                }
            }}
            className="text-foreground h-8 bg-transparent px-1.5 text-xs focus:outline-none"
        />
    );
}

export function DateTimeRangeFilter({
    label,
    icon: Icon,
    range,
    timeZone,
    onChange,
    showLabel = true,
}: {
    readonly label: string;
    readonly icon: ComponentType<{ readonly className?: string }>;
    readonly range: DateTimeRange;
    // IANA zone the inputs edit wall-clock time in; viewer-local when omitted.
    readonly timeZone?: string;
    readonly onChange: (range: DateTimeRange) => void;
    // Icon-only mode: the label still names the inputs for screen readers.
    readonly showLabel?: boolean;
}) {
    const hasRange = range.after !== undefined || range.before !== undefined;
    return (
        <div className="bg-background flex h-8 items-center overflow-hidden rounded-md border text-xs">
            <span className="text-muted-foreground flex items-center gap-1.5 pr-1 pl-2.5">
                <Icon className="h-3.5 w-3.5" />
                {showLabel ? label : null}
            </span>
            <DateTimeBoundInput
                label={`${label} after`}
                value={range.after}
                timeZone={timeZone}
                onCommit={(iso) => onChange({ ...range, after: iso })}
            />
            <span className="text-muted-foreground">–</span>
            <DateTimeBoundInput
                label={`${label} before`}
                value={range.before}
                timeZone={timeZone}
                onCommit={(iso) => onChange({ ...range, before: iso })}
            />
            {hasRange ? (
                <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 rounded-none px-1.5"
                    aria-label={`Clear ${label} filter`}
                    // Keep focus (and the blur-commit it would trigger) on a
                    // datetime input being edited, so clearing wins the race.
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() =>
                        onChange({ after: undefined, before: undefined })
                    }
                >
                    <X className="h-3 w-3" />
                </Button>
            ) : null}
        </div>
    );
}
